import { Paper, Switch, TextField, Tooltip, Typography } from '@mui/material'

import { Box } from '@/components/ui'

import { requirementDisplay } from '../../utils/requirementDisplay'

/**
 * Phrased like the product flags that sit behind another one, because to the operator
 * it is the same rule — this one just happens to need a value rather than a switch.
 */
const REQUIREMENT_NOTE = 'Requires an account SID'

const NEEDS_SID_MESSAGE =
  'Enter the account SID first — Twilio cannot be reached without it.'

/** "AC" followed by 32 hex characters, and the maximum the endpoint accepts. */
const ACCOUNT_SID_LENGTH = 34

export interface TwilioAccountSidRowProps {
  label: string
  checked: boolean
  accountSid: string
  isEditable: boolean
  /**
   * Whether a subaccount would be reached once saved. Not read off `accountSid`: an
   * emptied field cannot clear the SID the organization already has.
   */
  isRequirementMet: boolean
  errorMessage?: string
  onCheckedChange: (checked: boolean) => void
  onAccountSidChange: (accountSid: string) => void
}

/**
 * Texting, which needs the organization's Twilio subaccount identifier as well as
 * a switch.
 *
 * The switch stays out of reach until there is a subaccount to reach, because the
 * integration identifies it by this value and can look nothing up without one. That
 * requirement is presented exactly as a product flag's is — named on the row and
 * coloured while it is unmet — because to the operator it is the same rule, and this is
 * simply the one row whose requirement is a value rather than another switch.
 *
 * Whether it is met is decided by the caller, not by the field: clearing the field to
 * retype it does not take the organization's subaccount away. Both are saved together
 * with the rest of the product flags — the SID is a staged value here, not its own
 * write.
 */
export const TwilioAccountSidRow = ({
  label,
  checked,
  accountSid,
  isEditable,
  isRequirementMet,
  errorMessage,
  onCheckedChange,
  onAccountSidChange,
}: TwilioAccountSidRowProps) => {
  const { isBlocked, color: requirementColor } = requirementDisplay({
    isEditable,
    isRequirementMet,
    checked,
  })
  const canToggle = isEditable && !isBlocked
  // Gated on the SID being missing rather than on the switch being out of reach: a row
  // locked because a lower package is selected would otherwise be told to enter a SID
  // it may already have.
  const toggleHint = isBlocked ? NEEDS_SID_MESSAGE : ''

  return (
    <Paper variant='infoRow'>
      <Box variant='flexBetween' gap='md'>
        <Box variant='flexColumn' gap='xs' fullWidth={false}>
          <Typography
            variant='subtitle2'
            color={isEditable ? 'text.primary' : 'text.disabled'}
          >
            {label}
          </Typography>
          {/* Stated on the row rather than only in the switch's tooltip, matching the
              product flags that sit behind another one: a hover hides the requirement
              from an operator who has not thought to look for it. */}
          <Typography variant='caption' color={requirementColor}>
            {REQUIREMENT_NOTE}
          </Typography>
        </Box>

        <Box variant='flex' gap='md' fullWidth={false}>
          <TextField
            size='small'
            label='Account SID'
            placeholder='Enter account SID...'
            value={accountSid}
            disabled={!isEditable}
            error={errorMessage !== undefined}
            helperText={errorMessage}
            // An account SID is a fixed 34 characters, so the field is sized to show
            // one whole rather than scrolling it, and capped at the length the
            // endpoint accepts.
            slotProps={{
              htmlInput: { size: ACCOUNT_SID_LENGTH + 2, maxLength: ACCOUNT_SID_LENGTH },
            }}
            onChange={(event) => onAccountSidChange(event.target.value)}
          />
          {/* A disabled switch swallows pointer events, so the hint goes on a
              wrapper rather than on the control itself. */}
          <Tooltip title={toggleHint} placement='top-end'>
            <span>
              <Switch
                checked={checked}
                disabled={!canToggle}
                slotProps={{ input: { 'aria-label': label } }}
                onChange={(event) => onCheckedChange(event.target.checked)}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  )
}
